import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, Loader2, Search, Mail, Gift, Package, Calendar,
  Download, Send, Users, FileText, PartyPopper, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  awaiting_payment: "Awaiting payment",
  paid: "Paid · roles pending",
  locked: "Kit locked",
  delivered: "Delivered",
  scheduled: "Scheduled",
  redeemed: "Redeemed",
  partially_spent: "Partially spent",
  voided: "Voided",
};

const Library = () => {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState({});

  const lookUp = async (e) => {
    e?.preventDefault();
    const value = (email || "").trim().toLowerCase();
    if (!value) {
      setError("Enter the email you used to buy or receive a case.");
      return;
    }
    setLoading(true); setError(""); setData(null);
    try {
      const { data: body } = await api.post("/library/lookup", { email: value });
      setData(body);
      // Soft nudge if the inbox is empty — no email-existence leak either way.
      const total = (body.orders_as_host?.length || 0)
        + (body.orders_as_recipient?.length || 0)
        + (body.gifts_bought?.length || 0)
        + (body.gifts_received?.length || 0);
      if (total === 0) {
        setError("No cases, gifts, or packs found for that email. Check the inbox you used at checkout.");
      }
    } catch (err) {
      if (err?.response?.status === 429) setError("Too many lookups. Wait a few minutes.");
      else setError("Lookup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const resendOrder = async (orderId) => {
    setResending((s) => ({ ...s, [`o:${orderId}`]: true }));
    try {
      await api.post(`/library/orders/${orderId}/resend`, { email });
      toast.success("Email resent — check your inbox.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not resend. Try again shortly.";
      toast.error(typeof msg === "string" ? msg : "Could not resend.");
    } finally {
      setResending((s) => ({ ...s, [`o:${orderId}`]: false }));
    }
  };

  const resendGift = async (cardId) => {
    setResending((s) => ({ ...s, [`g:${cardId}`]: true }));
    try {
      await api.post(`/library/gift-cards/${cardId}/resend`, { email });
      toast.success("Gift resent to the recipient.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not resend. Try again shortly.";
      toast.error(typeof msg === "string" ? msg : "Could not resend.");
    } finally {
      setResending((s) => ({ ...s, [`g:${cardId}`]: false }));
    }
  };

  return (
    <div className="grain min-h-screen" data-testid="library-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-10"
          data-testid="library-back">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="mb-10">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg leading-none mb-2">
            all your evidence in one place
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec] mb-3">
            My Library
          </h1>
          <p className="text-[#a39797] text-sm max-w-xl">
            Every case, gift card, and party pack tied to your email — with resend buttons
            so you never have to dig through your inbox again.
          </p>
        </div>

        <form onSubmit={lookUp} className="card-noir p-6 mb-10" data-testid="library-lookup-form">
          <label className="text-xs tracking-widest text-[#a39797] mb-2 block">YOUR EMAIL</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5050]" />
              <input
                type="email"
                autoComplete="email"
                className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md pl-10 pr-4 py-3 text-[#f4f0ec] text-sm focus:outline-none focus:border-[#d4af37]"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="library-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary justify-center"
              data-testid="library-lookup-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Find my cases
            </button>
          </div>
          {error && (
            <div className="mt-3 text-sm text-[#d4af37]" data-testid="library-error">{error}</div>
          )}
        </form>

        {data && (
          <div className="space-y-10">
            {/* Orders — as host */}
            {data.orders_as_host?.length > 0 && (
              <Section icon={<FileText size={14} />} title="Your cases">
                {data.orders_as_host.map((o) => (
                  <OrderRow
                    key={o.order_id}
                    order={o}
                    onResend={() => resendOrder(o.order_id)}
                    resending={resending[`o:${o.order_id}`]}
                  />
                ))}
              </Section>
            )}

            {/* Orders — as gift recipient */}
            {data.orders_as_recipient?.length > 0 && (
              <Section icon={<Gift size={14} />} title="Gifts sent to you">
                {data.orders_as_recipient.map((o) => (
                  <OrderRow
                    key={o.order_id}
                    order={o}
                    recipient
                    onResend={() => resendOrder(o.order_id)}
                    resending={resending[`o:${o.order_id}`]}
                  />
                ))}
              </Section>
            )}

            {/* Gift cards bought */}
            {data.gifts_bought?.length > 0 && (
              <Section icon={<Send size={14} />} title="Gift cards you sent">
                {data.gifts_bought.map((g) => (
                  <GiftRow
                    key={g.card_id}
                    gift={g}
                    sent
                    onResend={() => resendGift(g.card_id)}
                    resending={resending[`g:${g.card_id}`]}
                  />
                ))}
              </Section>
            )}

            {/* Gift cards received */}
            {data.gifts_received?.length > 0 && (
              <Section icon={<Gift size={14} />} title="Gift cards sent to you">
                {data.gifts_received.map((g) => (
                  <GiftRow key={g.card_id} gift={g} received />
                ))}
              </Section>
            )}

            {/* Party packs */}
            {data.party_packs?.length > 0 && (
              <Section icon={<PartyPopper size={14} />} title="Party packs">
                {data.party_packs.map((p, i) => (
                  <PackRow key={`${p.order_id}-${i}`} pack={p} />
                ))}
              </Section>
            )}
          </div>
        )}

        <div className="mt-16 text-center text-xs text-[#5a5050]">
          Can't find something? Email{" "}
          <a href="mailto:support@wasntme.party" className="text-[#d4af37]">support@wasntme.party</a>
        </div>
      </div>
    </div>
  );
};

const Section = ({ icon, title, children }) => (
  <div data-testid={`library-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-3 text-xs tracking-widest text-[#a39797]">
      {icon} {title.toUpperCase()}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const StatusChip = ({ status }) => {
  const label = STATUS_LABEL[status] || status;
  const tone =
    status === "locked" || status === "delivered" || status === "redeemed"
      ? "bg-[#2d4f2d]/40 text-[#9fd79f] border-[#2d4f2d]"
      : status === "voided"
      ? "bg-[#4f2d2d]/40 text-[#d79f9f] border-[#4f2d2d]"
      : "bg-[#2d2424] text-[#a39797] border-[#2d2424]";
  return (
    <span className={`inline-block text-[10px] tracking-wider px-2 py-1 rounded border ${tone}`}>
      {label.toUpperCase()}
    </span>
  );
};

const OrderRow = ({ order, recipient, onResend, resending }) => (
  <div className="card-noir p-5 flex items-start justify-between gap-4 flex-wrap"
    data-testid={`library-order-${order.order_id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <div className="font-display text-xl text-[#f4f0ec] truncate">{order.case_title}</div>
        <StatusChip status={order.status} />
      </div>
      <div className="text-xs text-[#a39797] flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <Users size={12} /> {order.group_size || "—"} players
        </span>
        {order.event_date && (
          <span className="inline-flex items-center gap-1">
            <Calendar size={12} /> {order.event_date}
          </span>
        )}
        <span className="opacity-60">#{(order.order_id || "").slice(0, 8).toUpperCase()}</span>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      {order.host_token && (order.status === "locked" || order.status === "delivered") && (
        <Link
          to={`/dashboard/${order.host_token}`}
          className="btn-ghost text-xs"
          data-testid={`library-order-${order.order_id}-dashboard`}
        >
          <Download size={12} /> Dashboard
        </Link>
      )}
      {order.host_token && !recipient && (
        <Link
          to={`/dashboard/${order.host_token}`}
          className="btn-ghost text-xs"
          data-testid={`library-order-${order.order_id}-open`}
        >
          Open <ChevronRight size={12} />
        </Link>
      )}
      {!recipient && (
        <button
          onClick={onResend}
          disabled={resending}
          className="btn-primary text-xs"
          data-testid={`library-order-${order.order_id}-resend`}
        >
          {resending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Resend email
        </button>
      )}
    </div>
  </div>
);

const GiftRow = ({ gift, sent, received, onResend, resending }) => {
  const dollars = ((gift.balance_cents || 0) / 100).toFixed(2);
  const initial = ((gift.initial_cents || 0) / 100).toFixed(2);
  return (
    <div className="card-noir p-5 flex items-start justify-between gap-4 flex-wrap"
      data-testid={`library-gift-${gift.card_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <div className="font-display text-xl text-[#f4f0ec]">
            {gift.kind === "stored_value" ? `$${dollars}` : "Gifted case"}
          </div>
          <StatusChip status={gift.status} />
        </div>
        <div className="text-xs text-[#a39797] mb-1">
          {sent && <>To {gift.recipient_name || gift.recipient_email || "recipient"}</>}
          {received && <>From {gift.sender_name || "a friend"}</>}
        </div>
        <div className="text-xs text-[#5a5050] font-mono tracking-widest">
          {gift.pretty_code}
        </div>
        {gift.kind === "stored_value" && gift.balance_cents < gift.initial_cents && (
          <div className="text-[11px] text-[#a39797] mt-1">
            Original ${initial} · Remaining ${dollars}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {received && (
          <Link
            to={`/gifts/redeem?code=${gift.pretty_code.replace(/\s/g, "")}`}
            className="btn-primary text-xs"
            data-testid={`library-gift-${gift.card_id}-redeem`}
          >
            Redeem <ChevronRight size={12} />
          </Link>
        )}
        {sent && onResend && (
          <button
            onClick={onResend}
            disabled={resending}
            className="btn-primary text-xs"
            data-testid={`library-gift-${gift.card_id}-resend`}
          >
            {resending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Resend to recipient
          </button>
        )}
      </div>
    </div>
  );
};

const PackRow = ({ pack }) => {
  const dollars = ((pack.price_cents || 0) / 100).toFixed(2);
  return (
    <div className="card-noir p-5 flex items-start justify-between gap-4 flex-wrap"
      data-testid={`library-pack-${pack.pack_id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-display text-xl text-[#f4f0ec] mb-1 truncate">{pack.title}</div>
        <div className="text-xs text-[#a39797]">
          Bought with case #{(pack.order_id || "").slice(0, 8).toUpperCase()} · ${dollars}
        </div>
      </div>
      {pack.host_token && (
        <Link
          to={`/dashboard/${pack.host_token}`}
          className="btn-ghost text-xs"
          data-testid={`library-pack-${pack.pack_id}-open`}
        >
          <Download size={12} /> Dashboard
        </Link>
      )}
    </div>
  );
};

export default Library;
